import { Box, Center, Input, SimpleGrid, Spinner } from '@chakra-ui/react';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Text from 'components/Text';

import {
  fetchLucideIcons,
  getCachedIcons,
  lucideToSvgHtml,
  searchLucideIcons,
} from './lucideIcons';

// Inline SVG paths for popular icons (viewBox="0 0 24 24")
const ICON_SVG_DATA: Record<string, string> = {
  home: 'M12 3l9 8h-3v9h-5v-6H11v6H6v-9H3l9-8z',
  user: 'M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v2h20v-2c0-3.3-6.7-5-10-5z',
  search: 'M15.5 14h-.79l-.28-.27A6.47 6.47 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z',
  menu: 'M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z',
  close: 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z',
  check: 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z',
  'arrow-right': 'M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z',
  'arrow-left': 'M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z',
  'arrow-up': 'M4 12l1.41 1.41L11 7.83V20h2V7.83l5.59 5.58L20 12l-8-8-8 8z',
  'arrow-down': 'M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.59-5.58L4 12l8 8 8-8z',
  star: 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z',
  heart: 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z',
  settings: 'M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.49.49 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.48.48 0 00-.48-.41h-3.84a.48.48 0 00-.48.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 00-.59.22L2.74 8.87a.48.48 0 00.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.48-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1115.6 12 3.6 3.6 0 0112 15.6z',
  mail: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
  phone: 'M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z',
  calendar: 'M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z',
  clock: 'M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z',
  lock: 'M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z',
  eye: 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z',
  edit: 'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z',
  trash: 'M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z',
  download: 'M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z',
  upload: 'M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z',
  share: 'M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z',
  link: 'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z',
  image: 'M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z',
  video: 'M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z',
  music: 'M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z',
  file: 'M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z',
  folder: 'M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z',
  plus: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
  minus: 'M19 13H5v-2h14v2z',
  refresh: 'M17.65 6.35A7.96 7.96 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z',
  filter: 'M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z',
  bolt: 'M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z',
  globe: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z',
  'log-out': 'M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z',
  copy: 'M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z',
  code: 'M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z',
  info: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z',
  warning: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
  notification: 'M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z',
  camera: 'M12 10.8a3.2 3.2 0 100 6.4 3.2 3.2 0 000-6.4zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z',
};

const ICON_NAMES = Object.keys(ICON_SVG_DATA);

const POPULAR_ICONS = [
  'home', 'user', 'search', 'heart', 'star', 'settings',
  'close', 'check', 'arrow-right', 'arrow-left', 'mail', 'phone',
  'edit', 'trash', 'download', 'upload', 'share', 'link',
  'image', 'code', 'eye', 'lock', 'globe', 'plus',
];

// Max Lucide results to render at once for performance
const MAX_SEARCH_RESULTS = 60;

const IconPreview = ({ iconName }: { iconName: string }) => {
  const pathData = ICON_SVG_DATA[iconName];
  if (!pathData) return null;

  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="currentColor"
      style={{ display: 'block', margin: '0 auto' }}
    >
      <path d={pathData} />
    </svg>
  );
};

/** Renders a Lucide icon from its node array. Lucide uses stroke-based icons. */
const LucideIconPreview = ({ nodes }: { nodes: [string, Record<string, string>][] }) => {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block', margin: '0 auto' }}
    >
      {nodes.map(([tag, attrs], i) => {
        const Tag = tag as keyof JSX.IntrinsicElements;
        return <Tag key={i} {...attrs} />;
      })}
    </svg>
  );
};

const generateSvgHtml = (iconName: string): string => {
  const pathData = ICON_SVG_DATA[iconName];
  return `<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="${pathData}" /></svg>`;
};

const IconsTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [lucideResults, setLucideResults] = useState<string[]>([]);
  const [isLoadingLucide, setIsLoadingLucide] = useState(false);
  const [lucideCount, setLucideCount] = useState<number | null>(null);
  const { toastSuccess } = useToastWrapper();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Preload Lucide icons on mount so search is instant
  useEffect(() => {
    let mounted = true;
    fetchLucideIcons().then((data) => {
      if (data && mounted) setLucideCount(Object.keys(data).length);
    });
    return () => { mounted = false; };
  }, []);

  // Debounced search of Lucide icons
  const searchLucide = useCallback((query: string) => {
    if (!query.trim()) {
      setLucideResults([]);
      setIsLoadingLucide(false);
      return;
    }

    setIsLoadingLucide(true);

    // Check if already cached
    const cached = getCachedIcons();
    if (cached) {
      const results = searchLucideIcons(cached, query);
      setLucideResults(results.slice(0, MAX_SEARCH_RESULTS));
      setIsLoadingLucide(false);
      return;
    }

    // Fetch and then search
    fetchLucideIcons().then((data) => {
      if (data) {
        setLucideCount(Object.keys(data).length);
        const results = searchLucideIcons(data, query);
        setLucideResults(results.slice(0, MAX_SEARCH_RESULTS));
      }
      setIsLoadingLucide(false);
    });
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchLucide(value), 200);
  };

  // Filter built-in icons by search query
  const filteredBuiltIn = useMemo(() => {
    if (!searchQuery) {
      const rest = ICON_NAMES.filter((icon) => !POPULAR_ICONS.includes(icon));
      return [...POPULAR_ICONS, ...rest];
    }
    return ICON_NAMES.filter((icon) =>
      icon.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery]);

  // Filter out Lucide results that overlap with built-in icon names
  const filteredLucide = useMemo(() => {
    const builtInSet = new Set(ICON_NAMES.map((n) => n.toLowerCase()));
    return lucideResults.filter((name) => !builtInSet.has(name));
  }, [lucideResults]);

  const handleCopyBuiltIn = (iconName: string) => {
    const svg = generateSvgHtml(iconName);
    navigator.clipboard.writeText(svg);
    toastSuccess(`Copied ${iconName} SVG to clipboard!`);
  };

  const handleCopyLucide = (iconName: string) => {
    const icons = getCachedIcons();
    if (!icons || !icons[iconName]) return;
    const svg = lucideToSvgHtml(iconName, icons[iconName]);
    navigator.clipboard.writeText(svg);
    toastSuccess(`Copied ${iconName} SVG to clipboard!`);
  };

  const isSearching = searchQuery.trim().length > 0;
  const totalCount = lucideCount ? 45 + lucideCount : null;

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Text fontSize="large" fontWeight="semibold" mb={2}>
          Icons
        </Text>
        <Text fontSize="small" opacity="secondary">
          Click an icon to copy its SVG. Paste directly into your HTML.
        </Text>
      </Box>

      {/* Search */}
      <Input
        placeholder={totalCount ? `Search ${totalCount.toLocaleString()}+ icons...` : 'Search icons...'}
        value={searchQuery}
        onChange={(e) => handleSearchChange(e.target.value)}
        mb={4}
        size="sm"
        bg="whiteAlpha.100"
        border="none"
        _focus={{ bg: 'whiteAlpha.200', boxShadow: 'none' }}
      />

      {/* Built-in icon grid */}
      {filteredBuiltIn.length > 0 && (
        <>
          {isSearching && filteredLucide.length > 0 && (
            <Text fontSize="micro" opacity="tertiary" mb={2} fontWeight="medium">
              BUILT-IN
            </Text>
          )}
          <SimpleGrid columns={6} spacing={2}>
            {filteredBuiltIn.map((iconName) => (
              <Box
                key={iconName}
                p={2}
                borderRadius="md"
                bg="whiteAlpha.100"
                cursor="pointer"
                textAlign="center"
                _hover={{ bg: 'whiteAlpha.200', transform: 'scale(1.05)' }}
                transition="all 0.15s"
                onClick={() => handleCopyBuiltIn(iconName)}
                title={iconName}
              >
                <Center mb={1} color="whiteAlpha.900">
                  <IconPreview iconName={iconName} />
                </Center>
                <Box display="block" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" color="whiteAlpha.600" style={{ fontSize: '8px' }}>
                  {iconName}
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        </>
      )}

      {/* Lucide search results */}
      {isSearching && (
        <>
          {isLoadingLucide && (
            <Center py={4}>
              <Spinner size="sm" color="whiteAlpha.600" />
            </Center>
          )}

          {!isLoadingLucide && filteredLucide.length > 0 && (
            <>
              <Text fontSize="micro" opacity="tertiary" mt={4} mb={2} fontWeight="medium">
                LUCIDE ICONS
              </Text>
              <SimpleGrid columns={6} spacing={2}>
                {filteredLucide.map((iconName) => {
                  const icons = getCachedIcons();
                  const nodes = icons?.[iconName];
                  if (!nodes) return null;
                  return (
                    <Box
                      key={`lucide-${iconName}`}
                      p={2}
                      borderRadius="md"
                      bg="whiteAlpha.100"
                      cursor="pointer"
                      textAlign="center"
                      _hover={{ bg: 'whiteAlpha.200', transform: 'scale(1.05)' }}
                      transition="all 0.15s"
                      onClick={() => handleCopyLucide(iconName)}
                      title={iconName}
                    >
                      <Center mb={1} color="whiteAlpha.900">
                        <LucideIconPreview nodes={nodes} />
                      </Center>
                      <Box display="block" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" color="whiteAlpha.600" style={{ fontSize: '8px' }}>
                        {iconName}
                      </Box>
                    </Box>
                  );
                })}
              </SimpleGrid>
              {lucideResults.length > MAX_SEARCH_RESULTS && (
                <Text fontSize="micro" opacity="tertiary" textAlign="center" mt={2}>
                  Showing {MAX_SEARCH_RESULTS} of {lucideResults.length} results. Refine your search.
                </Text>
              )}
            </>
          )}
        </>
      )}

      {/* Empty state */}
      {filteredBuiltIn.length === 0 && !isLoadingLucide && filteredLucide.length === 0 && (
        <Text fontSize="small" opacity="secondary" textAlign="center" py={8}>
          No icons found. Try a different search term.
        </Text>
      )}
    </Box>
  );
};

export default IconsTab;
